import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useMemo, useState } from 'react';
import { usePusherEvent } from '../../../hooks/use-pusher-event';
import { setPrintDocumentFormat } from '../../../store/printing/print.actions';
import { generatePackingSlipAsync } from '../../../store/printing/print.api';
import { NotificationService } from 'ui-components/services/notification-service';
import { packingSlip } from '../../../store/printing/document-type-epic-helpers/packing-slip';
import { selectPackingSlipTemplates, selectSelectedPackingSlipTemplate } from '../../../store/printing/packing-slip-template-selectors';
import { BULK_PRINT } from '../../../services/pusher-service/pusher-contract-messages';
import { BULK_PRINT_TIMEOUT_MS } from '../../../services/pusher-service/timeouts';
import { useIntl } from 'react-intl';
import { messages } from './print-modal-content/messages';

const usePackingSlipTemplate = (fulfillmentPlanIds, setIsLoading, onTemplateLoaded) => {
  const [jobIds, setJobIds] = useState([]);
  const [templateChangeTimeoutId, setTemplateChangeTimeoutId] = useState(0);
  const [didTemplateChangeTimeOut, setDidTemplateChangeTimeOut] = useState(false);
  const intl = useIntl();

  const storeDefaultOption = useMemo(
    () => ({
      name: intl.formatMessage(messages.defaultPackingSlipTemplateOption),
    }),
    [intl],
  );

  const templates = useSelector(selectPackingSlipTemplates(storeDefaultOption));
  const selectedTemplate = useSelector(selectSelectedPackingSlipTemplate(templates, fulfillmentPlanIds));

  const dispatch = useDispatch();

  const changeTemplate = useCallback(
    (newTemplate) => {
      if (newTemplate.packingSlipId !== selectedTemplate.packingSlipId) {
        setIsLoading(true);

        dispatch(setPrintDocumentFormat(newTemplate));

        const showFailureNotification = () => {
          const notification = packingSlip.getAsyncPrintFailedNotificationProps(fulfillmentPlanIds.length);
          NotificationService.showNotification(notification);
        };

        generatePackingSlipAsync(fulfillmentPlanIds, newTemplate.packingSlipId)
          .then((data) => {
            setJobIds(data);
            setDidTemplateChangeTimeOut(false);
            const timeoutId = setTimeout(() => {
              showFailureNotification();
              setDidTemplateChangeTimeOut(true);
            }, BULK_PRINT_TIMEOUT_MS);
            setTemplateChangeTimeoutId(timeoutId);
          })
          .catch(showFailureNotification);
      }
    },
    [dispatch, fulfillmentPlanIds, selectedTemplate.packingSlipId, setIsLoading],
  );

  const onBulkPrintPusherMessageReceived = useCallback(
    (pusherMessage) => {
      if (!jobIds.includes(pusherMessage.messageGuid) || didTemplateChangeTimeOut) {
        return;
      }

      const { DownloadUrl } = pusherMessage.data;

      if (DownloadUrl) {
        clearTimeout(templateChangeTimeoutId);
        onTemplateLoaded(DownloadUrl);
      }
    },
    [jobIds, didTemplateChangeTimeOut, templateChangeTimeoutId, onTemplateLoaded],
  );

  usePusherEvent(BULK_PRINT, onBulkPrintPusherMessageReceived);

  return {
    templates,
    selectedTemplate,
    changeTemplate,
  };
};

export { usePackingSlipTemplate };
